import type { BlueMarlinState, CoralBottom, CoralState, FishState, GameEvent, GrayWhaleState, HeatStressState, HeatwaveState, LeatherbackState, Migrants, PlaceSst, PlanktonState, ReefFish, RegionState, RightWhaleState, Sanctuary, SealState, Seals, SeaTurtles, TurtleState, WhaleState, Whales, World } from './types';
import { regionEnvironment, sanctuaryEnvironment } from './systems/environment';
import { openingClimate, openingHeatwave, openingSst, regionRecord, sanctuaryRecord } from './systems/climate';
import { openingHeatStress } from './systems/heatStress';
import { sanctuaryOpeningCoral } from './systems/coral';
import { CORAL_KINDS } from './data/reef/kinds';
import { openingRegionPlankton, sanctuaryOpeningPlankton, sitePlanktonPlace } from './systems/plankton';
import { sanctuaryOpeningFish, siteHasFish } from './systems/fish';
import { openingTurtles, sanctuaryOpeningTurtles, siteHasTurtles, turtleDraws, TURTLES } from './systems/turtles';
import { stepHonu, turtleClasses } from './turtles';
import { openingWhales, sanctuaryOpeningWhales, siteHasWhales, whaleDraws } from './systems/whales';
import { HUMPBACK, stepHumpback, whaleClasses } from './whales';
import { HUMPBACK_SUPPLY } from './data/whales/humpback';
import { openingSeals, sanctuaryOpeningSeals, sealDraws, siteHasSeals } from './systems/seals';
import { openingSharks, openingWhitetips, sharkDraws, whitetipDraws } from './systems/sharks';
import { WHITETIP_SHARK } from './whitetips';
import { TIGER_SHARK, sharkClasses, siteHasTigerSharks, stepTigerSharks } from './sharks';
import { MONK_SEAL, sealClasses, stepMonkSeals } from './seals';
import { deep7Normal, openingShrimp, openingSlope, siteHasSlope, stepShrimpDay, stepSlopeDay } from './systems/slope';
import { SHRIMP } from './shrimp';
import { DEEP7_SPECIES } from './bottomfish';
import { openingRightWhales, rightWhaleDraws } from './systems/rightwhales';
import { openingMigrants } from './systems/migrants';
import { held } from './held';
import { blueMarlinDraws, openingBlueMarlin } from './systems/marlin';
import { NORTH_ATLANTIC_RIGHT_WHALE, rightWhaleClasses, stepRightWhales } from './rightwhales';
import { RIGHT_WHALE_SUPPLY } from './data/rightwhales/narw';
import { PACIFIC_BLUE_MARLIN, recruitsOf, stepBlueMarlin } from './marlin';
import { BLUE_MARLIN_SUPPLY } from './data/marlin/pacific';
import { leatherbackDraws, openingLeatherbacks } from './systems/leatherback';
import { WEST_PACIFIC_LEATHERBACK, leatherbackClasses, stepLeatherbacks } from './leatherback';
import { LEATHERBACK_SUPPLY } from './data/leatherbacks/westpacific';
import { grayWhaleDraws, openingGrayWhales } from './systems/graywhales';
import { EASTERN_NORTH_PACIFIC_GRAY_WHALE, grayWhaleClasses, stepGrayWhales } from './graywhales';
import { GRAY_WHALE_SUPPLY } from './data/graywhales/enp';
import { BULLETHEAD, FISH_ISLANDS, FISH_LIVES, KALA, MANINI, MOANOKEA, NENUE, OMILU, PALENOSE, REDLIP, ROI, TAAPE, ULUA, ageClasses, type FishLife, type FishSpeciesId } from './fish';
import { reefOf } from './data/sites';
import { TAG_HABITATS, emptyTags, type TagHabitat, type TaggedAnimal, type TaggedGroup, type Tags } from './tags';
import { nameKept } from './commands';
import { REEF_ISLANDS, DEPTH_BINS } from './reef';
import { DHW_WINDOW_DAYS } from './heatStress';
import { STREAM, carriedStreamSeed } from './streams';
import { stepAnimalStage, stepEnvironmentStage, stepProductionStage, tickMany } from './sim';
import { REGIONS } from './data/oceans';
import { SITES, siteIdOfPlace } from './data/sites';
import { DEFAULT_START_EPOCH_DAY, createWorld } from './world';
import { PROGRAMS, openingMonitoring, surveyOn, type SurveyResult } from './monitoring';
import { REEF_BOTTOM } from './data/reef/bottom';
import { civilFromEpochDay, epochDayOf } from './calendar';
import { Rng } from './rng';
import { LIMIT_GROUPS, LIMIT_OPTIONS, PATROL_LEVELS, hawaiiLimits, openingManagement, type Management } from './management';
import { LEDGER_LINES, SURVEY_NAMES, checkedRule, islandSurvey, mostOf, replayedOperations, type Ledger, type Operations } from './operations';
import { AREA_COUNTS_KEPT, COMMUNITY_COUNTS_KEPT } from './areas';
import type { SurveyKind } from './economy';
import { ECONOMY } from './economy';
import { CHAPTERS, MARKS, OBJECTIVES, RETIRED_OBJECTIVES, REVIEW_LINES, openingCampaign, type Campaign } from './campaign';

/**
 * Saves.
 *
 * The world is plain data, so a save is JSON plus a version stamp.
 * `SAVE_VERSION` is bumped whenever the shape of `World` changes, and
 * `migrate` gets an arm for the old shape. **A migration without a test is a
 * guess**: before a bump, a real save of the old version is recorded under
 * `tests/fixtures/`, and `tests/save.test.ts` loads it.
 *
 * This file turns a world into bytes and back. It does not know where the
 * bytes go: that is `src/platform/saves.ts`.
 */

export const SAVE_VERSION = 45;

export interface SaveFile {
  version: number;
  /**
   * When the save was written, supplied by the caller. The core may not read
   * the clock, and a timestamp taken here would make `serialise` impure: the
   * same world would write different bytes on every call.
   */
  savedAt: string;
  world: World;
}

export function serialise(world: World, savedAt = ''): string {
  const save: SaveFile = { version: SAVE_VERSION, savedAt, world };
  return JSON.stringify(save);
}

export function deserialise(json: string): World {
  const parsed = JSON.parse(json) as Partial<SaveFile>;
  if (typeof parsed.version !== 'number' || !parsed.world) {
    throw new Error('Not a valid save file.');
  }
  if (parsed.version > SAVE_VERSION) {
    throw new Error(`This save was written by a newer version of the game (v${parsed.version}).`);
  }
  try {
    return migrate(parsed.world, parsed.version);
  } catch (error) {
    // A file that is valid JSON with a version on it but is missing a section
    // throws a raw TypeError from inside a migration arm. Sable Reserve showed
    // players "Cannot read properties of undefined (reading 'map')" that way
    // before it named the fault here, once, rather than guarding every arm.
    // Deliberate errors are passed through as they are.
    if (error instanceof TypeError || error instanceof RangeError) throw damaged(parsed.version);
    throw error;
  }
}

/** Brings a world of version `from` up to `SAVE_VERSION`. */
function migrate(world: World, from: number): World {
  let current = { ...world };
  if (from < 1) throw new Error(`Save version ${from} was never written by this game.`);
  // Every world is checked for the fields every version has had, so a damaged
  // save fails here, by name, and not three ticks into play.
  if (!Array.isArray(current.sanctuaries) || !Array.isArray(current.log) || typeof current.day !== 'number') {
    throw damaged(from);
  }
  // The next id a whole number past every sanctuary's (review M149: a save without it loaded, and the next tag, M148, took
  // the id NaN, which the player's own next save was refused for); every version has held it since M1.
  if (!Number.isInteger(current.nextEntityId) || !current.sanctuaries.every((s) => current.nextEntityId > (s as { id: number }).id)) throw damaged(from);
  // Every region, once each, in the order of `REGIONS`, which the tick, the
  // world table and the fingerprint all keep (review M20: a save cut to one
  // region, or with them reversed, loaded).
  const regions = current.regions as unknown;
  if (from >= 2 && !(Array.isArray(regions) && regions.length === REGIONS.length
    && REGIONS.every((d, i) => (regions[i] as { id?: unknown } | undefined)?.id === d.id))) {
    throw damaged(from);
  }
  if (from >= 3 && typeof current.startEpochDay !== 'number') throw damaged(from);
  // A site the game does not know is refused here, by name, and not moved to
  // a site the game does know (review M18). The lookups after this (the site
  // to follow, the water) would meet it as a raw TypeError.
  if (from >= 4 && !current.sanctuaries.every((s) => typeof s.siteId === 'string' && Object.hasOwn(SITES, s.siteId))) {
    throw damaged(from);
  }
  // The climate's stream, which every version has carried (M2), and from v6
  // the climate, each place's reading and each sanctuary's heatwave (M24),
  // which only a later tick would find a hole in: a reading that is not whole
  // years back or not a number, or a heatwave's day that is not a day, would
  // read NaN for good rather than fail (review M25).
  if (!Number.isInteger(current.climateRngState)) throw damaged(from);
  // The ecology's stream, which every version has carried (M1), the fish drew
  // on from M74 to M80 and nothing draws on since (each stock its own,
  // `stockStream`): a world without it is not whole, and a system that drew
  // on it again would draw from no state.
  if (!Number.isInteger(current.rngState)) throw damaged(from);
  if (from >= 6 && !climateWhole(current)) throw damaged(from);
  // From v7, each sanctuary's heat stress (M46): a window that is not 84
  // numbers would read a wrong Degree Heating Weeks for twelve weeks.
  if (from >= 7 && !current.sanctuaries.every((s) => heatStressWhole(s.heatStress))) throw damaged(from);
  // From v9, each sanctuary's coral (M47; by depth, M49): there where its
  // site has a reef and only there, every stratum a cover, every depth its
  // bleaching and its answered heat. A v8 world's is grown again (below).
  if (from >= 9 && !current.sanctuaries.every((s) => coralWhole(s.coral, reefOf(s) !== null, from))) throw damaged(from);
  // From v10, each place's plankton (M52): every region's, and every
  // sanctuary's where its site has water of its own and only there, each
  // nutrient, living thing and the detritus a number not below 0, its mixed
  // layer deeper than nothing. A hole would read NaN in the water for good.
  if (from >= 10 && !(current.regions.every((r) => planktonWhole(r.plankton))
    && current.sanctuaries.every((s) => (sitePlanktonPlace(s.siteId) ? planktonWhole(s.plankton) : s.plankton === null)))) {
    throw damaged(from);
  }
  // From v11, each sanctuary's bullethead stock (M74), from v12 the
  // palenose's beside it (M82), from v13 the roi's (M84), from v14 the
  // kala's (M90), from v23 the ulua's and the ʻōmilu's (M126), from v24
  // the redlip's (M128) and from v25 the day octopus's (M129), each
  // version's own species: there where
  // its site has them (Maui Nui) and only there, every island's every age
  // class a whole count not below 0, and every slot of the larvae afloat a
  // number not below 0 for every island. A hole would read NaN in the fish
  // for good.
  if (from === 11 && !current.sanctuaries.every((s) => (siteHasFish(s.siteId) ? stockWhole(s.fish, BULLETHEAD) : s.fish === null))) throw damaged(from);
  if (from === 12 && !current.sanctuaries.every((s) => fishWhole(s.fish, siteHasFish(s.siteId), [BULLETHEAD, PALENOSE]))) throw damaged(from);
  if (from === 13 && !current.sanctuaries.every((s) => fishWhole(s.fish, siteHasFish(s.siteId), [BULLETHEAD, PALENOSE, ROI]))) throw damaged(from);
  if (from >= 14 && from <= 22 && !current.sanctuaries.every((s) => fishWhole(s.fish, siteHasFish(s.siteId), [BULLETHEAD, PALENOSE, ROI, KALA]))) throw damaged(from);
  if (from === 23 && !current.sanctuaries.every((s) => fishWhole(s.fish, siteHasFish(s.siteId), [BULLETHEAD, PALENOSE, ROI, KALA, ULUA, OMILU]))) throw damaged(from);
  if (from === 24 && !current.sanctuaries.every((s) => fishWhole(s.fish, siteHasFish(s.siteId), [BULLETHEAD, PALENOSE, ROI, KALA, ULUA, OMILU, REDLIP]))) throw damaged(from);
  if (from >= 25 && from <= 32 && !current.sanctuaries.every((s) => fishWhole(s.fish, siteHasFish(s.siteId), FISH_LIVES.filter((l) => l !== NENUE && l !== MOANOKEA && l !== MANINI && l !== TAAPE)))) throw damaged(from);
  // From v33 (M178), the nenue beside them; from v34 (M179), the moano kea; from v40 (M218, M220), the taʻape and the manini.
  if (from === 33 && !current.sanctuaries.every((s) => fishWhole(s.fish, siteHasFish(s.siteId), FISH_LIVES.filter((l) => l !== MOANOKEA && l !== MANINI && l !== TAAPE)))) throw damaged(from);
  if (from >= 34 && from <= 39 && !current.sanctuaries.every((s) => fishWhole(s.fish, siteHasFish(s.siteId), FISH_LIVES.filter((l) => l !== MANINI && l !== TAAPE)))) throw damaged(from);
  if (from >= 40 && !current.sanctuaries.every((s) => fishWhole(s.fish, siteHasFish(s.siteId), FISH_LIVES))) throw damaged(from);
  // From v15, each sanctuary's honu (M93): there where its site has them
  // (Maui Nui) and only there, every class at sea, at home and away a whole
  // count not below 0, and the hatchlings due a number not below 0; from v35 (M187) the hawksbill's beside it.
  if (from >= 15 && !current.sanctuaries.every((s) => turtlesWhole(s.turtles, siteHasTurtles(s.siteId), from))) throw damaged(from);
  // From v16, each sanctuary's humpbacks (M101): there where its site has
  // them (Maui Nui) and only there, every class a whole count not below 0.
  if (from >= 16 && !current.sanctuaries.every((s) => whalesWhole(s.whales, siteHasWhales(s.siteId)))) throw damaged(from);
  // From v17, each sanctuary's monk seals (M103): there where its site has
  // them (Maui Nui) and only there, every class and every day's nursing pups
  // a whole count not below 0.
  if (from >= 17 && !current.sanctuaries.every((s) => sealsWhole(s.seals, siteHasSeals(s.siteId)))) throw damaged(from);
  // v36 (M192): the deep slope's Deep 7, none where its site has none.
  if (from >= 36 && !current.sanctuaries.every((s) => slopeWhole(s.slope, siteHasSlope(s.siteId), from))) throw damaged(from);
  // v38 (M197): the tiger sharks, none where the site has none; an older save holds none (review M190: a save's
  // population its version never had is no save's).
  if (from >= 38 && !current.sanctuaries.every((s) => sharksWhole(s.sharks, siteHasTigerSharks(s.siteId), from))) throw damaged(from);
  if (from <= 37 && current.sanctuaries.some((s) => 'sharks' in s)) throw damaged(from);
  // From v18, the populations of no sanctuary (M107): the North Atlantic
  // right whales, every class of each sex a whole count not below 0; from
  // v19 the Pacific blue marlin too (M108), and its season's spawners a
  // weight not below 0; from v20 the western Pacific leatherbacks (M111),
  // and each season's nesters a whole count not below 0; from v21 the
  // eastern North Pacific gray whales (M112), the whales of one and over its
  // last two seasons opened with whole counts not below 0, and the season's
  // pregnancy rate a number not below 0.
  if (from >= 18 && !migrantsWhole(current.migrants, from)) throw damaged(from);
  // From v26 the tagged animals and groups (M148): each one's stock a reef's at a site with fish, its island and age
  // class within its stock's, its days whole, its death's cause one of the causes, its name kept as a tagging keeps it.
  // From v39 (M203) a tag may be a hawksbill's or a tiger shark's, and each tag and group names the drawn animal it was
  // given from, or none.
  if (from >= 26 && !tagsWhole(current.tags, current, from)) throw damaged(from);
  // From v27 (M160), the monitoring programs' results: each a program the game runs, every survey a day not after
  // today, oldest first, a value a finite number not below 0, and its error a coefficient of variation or none.
  if (from >= 27 && !monitoringWhole(current.monitoring, epochDayOf(current), from)) throw damaged(from);
  // From v28 (M165), each sanctuary's management: one for each sanctuary with reef fish and none without, its herbivore
  // protection a yes or no for each of the reef's islands and nothing else; from v30 (M170) its limits too, each fish
  // group's bag and size one of its options.
  if (from >= 28 && !current.sanctuaries.every((s) => managementWhole(s.management, siteHasFish(s.siteId), from, epochDayOf(current)))) throw damaged(from);
  // From v41 (M238), the operations: funds a whole number of dollars, each staff member a role's with a paid-to day,
  // each job a survey's or a rule's with its crew on the staff, its phases' days, its rule one a command may propose.
  // From v43 (M274) its plan, its areas' counts and its heat's choice, each job's island and planned day, and each
  // finished one's start, wait and island. From v45 (M307) a shore cleanup at sea, planned or home, its tonnes, and each
  // island's shore's reckoning.
  if (from >= 41 && !operationsWhole(current.operations, epochDayOf(current), from)) throw damaged(from);
  // From v42 (M239), the campaign: its chapter one of the four or past them, a day for each begun, each objective done
  // and each mark the campaign's own with a whole day, its test one of the two or none, its review whole or none.
  if (from >= 42 && !campaignWhole(current.campaign, epochDayOf(current))) throw damaged(from);

  // v25 -> v26 (M148): the tagged animals and groups, none in an older world; first, as the replays below tick the world.
  if (from <= 25) current = { ...current, tags: emptyTags() };

  // v1 -> v2 (M3): the twelve regions. A v1 world had none; their
  // environment, like every place's, is computed below for the save's day:
  // exactly what the tick that made it would have written. `tests/save.test.ts`
  // holds a migrated v1 save to the current world built the same way, byte for
  // byte, a hundred days on.
  if (from < 2) current = withKeyAfter(current, 'conservancyName', 'regions', REGIONS.map((r) => ({ id: r.id }) as RegionState));

  // v2 -> v3 (M12): the real calendar. A v2 world counted days on a 360-day
  // year from an undated "year 1". It keeps its day count, which is what the
  // simulation's ages and durations are made of, and is dated from
  // `DEFAULT_START`, so its day 0 is 1 January 2026; its date labels shift by
  // the difference between the two calendars, up to a few days a year.
  if (from < 3) current = withKeyAfter(current, 'day', 'startEpochDay', DEFAULT_START_EPOCH_DAY);

  // v3 -> v4 (M17): a sanctuary names its site, where its seafloor is found.
  // Every earlier save was written at one of the two openings, known by its
  // place name. The site's place follows below.
  if (from < 4) {
    current.sanctuaries = current.sanctuaries.map((s) => {
      const siteId = siteIdOfPlace(s.site?.placeName ?? '');
      if (siteId === null) throw damaged(from);
      // Rebuilt key by key, so the id sits where a new sanctuary has it.
      const { id, name, siteId: _none, ...rest } = s;
      const named: Sanctuary = { id, name, siteId, ...rest };
      return named;
    });
  }

  // v4 -> v5 (M19): every environment gains the water at the surface and the
  // depth of the mixed layer, computed below with the rest of it.

  // Every version: a sanctuary's place and region are its site's, which is
  // geography and moves when its table does (M17 pinned Maui Nui about 24 km
  // west of Lahaina, where v3 saves have it; a newer GEBCO could move it
  // again). The copies a save holds follow the site (review M18).
  current.sanctuaries = current.sanctuaries.map((s) => {
    const def = SITES[s.siteId];
    const same = s.regionId === def.regionId && s.site.placeName === def.site.placeName
      && s.site.latDeg === def.site.latDeg && s.site.lonDeg === def.site.lonDeg;
    return same ? s : { ...s, regionId: def.regionId, site: { ...def.site } };
  });

  // v5 -> v6 (M24): the climate. No world before v6 drew on its climate
  // stream, which is still where its seed put it, so the climate it would have
  // on the save's day is found by replaying it from the opening, as the tick
  // makes it (`replayEnvironment`): after the site is followed, so it is
  // replayed where the sanctuary is now (review M25: before, a save from a
  // site since moved replayed its old region's record).
  //
  // v6 -> v7 (M46): each reef's heat stress, which follows the departures day
  // by day. Nothing the player does moves the climate, so a v6 world's is the
  // one its seed gives: replayed from the opening on its climate stream as the
  // seed set it, it must come out as the save has it (or the save is
  // damaged), and the heat stress it followed is the world's.
  //
  // v7 -> v8 (M47): each reef's coral, which answers the heat day by day. A
  // v7 world is replayed the same way, its heat stress held to the save's
  // too, and takes the coral the replay grew.
  //
  // v8 -> v9 (M49): each depth's coral answers the heat in its own water. A
  // v8 world is replayed the same way and takes the coral the replay grew,
  // and the replay's coral entries in its log for v8's.
  //
  // v9 -> v10 (M52): each place's plankton, which lives day by day in its
  // water and light. A v9 world is replayed the same way and takes the
  // plankton the replay grew (and the coral the replay grew: v9's to the bit
  // until M68's spawning and refitted rates moved it, review M69).
  //
  // v10 -> v11 (M74): each sanctuary's bullethead stock. A v10 world is
  // replayed the same way and takes the fish the replay grew, drawn as the
  // tick draws them: on each island's own stream of the day since M80
  // (`stockStream`), the ecology's stream staying where the save has it (from
  // M74 to M80 they drew on the ecology's, the replay taking the stream it
  // left).
  if (from < 11) current = replayEnvironment(current, from);

  // v11 -> v12 (M82): the palenose parrotfish beside the bullethead; and
  // v12 -> v13 (M84): the roi, which eats them. Eaten, the parrotfish live
  // otherwise than a v11 or v12 world's did, and nothing a player does
  // reaches the fish yet (fishing and protection are Phase 4's and 5's): so a
  // v11 or v12 world takes the reef's fish a new game would have on its day,
  // every species opened with the world and stepped together on their own
  // streams to the save's day (`withReplayedFish`), as a v10 world takes the
  // replay's. v13 -> v14 (M90): the kala, which neither eats nor is eaten,
  // a v13 world taking it alone so (M90's `withKala`, since gone). v22 -> v23
  // (M126): the ulua and the ʻōmilu, which eat the parrotfish too, so that
  // they live otherwise again: a v13 to v22 world takes the reef's fish a
  // new game would have on its day the same way, every species replayed.
  // v23 -> v24 (M128): the redlip parrotfish, eaten by all three, so that
  // the others live otherwise again: a v11 to v23 world the same way.
  // v24 -> v25 (M129): the day octopus, eaten by the ulua in a hunt of its
  // own: the others live as they did (the predation density independent in
  // its prey, M128), so the replay gives a v24 world back its own fish, the
  // octopus beside them, on their own streams. Since M170 every v29 or older world's reef stocks are a new game's on its
  // day (`withReplayed`, below), so that replay is gone, its work done there.

  // v14 -> v15 (M93): the green sea turtle, the honu, which lives apart from
  // the reef's fish: a v11 to v14 world takes the honu a new game would have
  // on its day (its fish the replay's, above), opened with the world and
  // stepped on its own stream to the save's (`withTurtle`), as the tick steps
  // it; an older world takes the replay's (above).
  if (from >= 11 && from <= 14) current = withTurtle(current, 'honu');

  // v15 -> v16 (M101): the humpbacks, a population apart from the rest: a
  // v11 to v15 world takes the humpbacks a new game would have on its day,
  // opened with the world and stepped on their own stream to the save's
  // (`withHumpbacks`); an older world takes the replay's (above).
  if (from >= 11 && from <= 15) current = withHumpbacks(current);

  // v16 -> v17 (M103): the monk seals, a stock apart from the rest: a v11 to
  // v16 world takes the seals a new game would have on its day, opened with
  // the world and stepped on their own stream to the save's (`withMonkSeals`);
  // an older world takes the replay's (above). v21 -> v22 (M119): their pups
  // and pupping season from sources the game may carry, so a v17 to v21
  // world's seals, its old rates', are made again the same way.
  if (from >= 11 && from <= 21) current = withMonkSeals(current);

  // v17 -> v18 (M107): the North Atlantic right whales, the first population
  // of no sanctuary: a v11 to v17 world takes the whales a new game would
  // have on its day, opened with the world and stepped on their own stream
  // to the save's (`withRightWhales`); an older world takes the replay's
  // (above).
  if (from >= 11 && from <= 17) current = withRightWhales(current);

  // v18 -> v19 (M108): the Pacific blue marlin, the second population of no
  // sanctuary, after the right whales: a v11 to v18 world takes the marlin a
  // new game would have on its day, opened with the world and stepped on
  // their own stream to the save's (`withBlueMarlin`); an older world takes
  // the replay's (above).
  if (from >= 11 && from <= 18) current = withBlueMarlin(current);

  // v19 -> v20 (M111): the western Pacific leatherbacks, the third
  // population of no sanctuary, after the marlin: a v11 to v19 world takes
  // the leatherbacks a new game would have on its day, opened with the world
  // and stepped on their own stream to the save's (`withLeatherbacks`); an
  // older world takes the replay's (above).
  if (from >= 11 && from <= 19) current = withLeatherbacks(current);

  // v20 -> v21 (M112): the eastern North Pacific gray whales, the fourth
  // population of no sanctuary, after the leatherbacks: a v11 to v20 world
  // takes the gray whales a new game would have on its day, opened with the
  // world and stepped on their own stream to the save's (`withGrayWhales`);
  // an older world takes the replay's (above).
  if (from >= 11 && from <= 20) current = withGrayWhales(current);

  // Every version: the two floats a season's first day stores, the blue
  // marlin's spawners and the gray whales' pregnancy rate, held to six
  // places, as a world holds them since review M116; a save before it
  // carries their last bits (`held` leaves a held value as it is).
  current.migrants.blueMarlin.spawnersKg = held(current.migrants.blueMarlin.spawnersKg);
  current.migrants.grayWhale.calvingRate = held(current.migrants.grayWhale.calvingRate);

  // A v26 or older world (M160) takes the surveys a new game would have counted to its day, and a v25 to v27 world
  // (M165) the kala and the surveys' counts of them, its table refit at Maui's assessed fishing (an older world's fish
  // are replayed from the tables, above): a world from the same seed and first day, stepped to the save's (nothing a
  // player could do before v28 reached the animals; the kala neither eats nor is eaten, so no other stock moves with it).
  // A v28 world (M167) takes the coral, its bottom, the surveys' counts of it and the log's entries the same way,
  // unprotected: a v28 save's Herbivore Protection Areas reached the fish alone, and when each began is not saved, so
  // the coral's answer to them starts on loading (open; on Maui, seed 42, protected from the start and saved on day
  // 730, 2.84% coralline algae and grazed EAC where a current game protected as long holds 3.32%, MEASURED). A v29 or
  // older world (M170) takes every reef stock the same way, fished under Hawaiʻi's rules from its opening (its
  // parrotfish were fished at their assessed rate), and every survey's counts with them; unprotected, as above.
  if (from <= 29) current = withReplayed(current, from);

  // v27 -> v28 (M165): each sanctuary's management, nothing protected in an older world (its fish fished as they were).
  if (from <= 27) current = { ...current, sanctuaries: current.sanctuaries.map((s) => ({ ...s, management: openingManagement(siteHasFish(s.siteId)) })) };
  // v29 -> v30 (M170): each management's limits, Hawaiʻi's alone (none a sanctuary's, there being none before).
  if (from <= 29) current = { ...current, sanctuaries: current.sanctuaries.map((s) => (s.management ? { ...s, management: { ...s.management, limits: hawaiiLimits() } } : s)) };
  // v30 -> v31 (M171): each management's patrols, every island at Hawaiʻi's coverage (the world as it was).
  if (from <= 30) current = { ...current, sanctuaries: current.sanctuaries.map((s) => (s.management ? { ...s, management: { ...s.management, patrols: REEF_ISLANDS.map(() => 0) } } : s)) };
  // v42 -> v43 (M274): each management's patrol terms, none (every bought patrol until stopped, as it was), and its
  // levels moved up one past the new one-stretch level, so each keeps its coverage; before the replays below, which
  // read them.
  if (from <= 42) {
    current = { ...current, sanctuaries: current.sanctuaries.map((s) => (s.management ? { ...s, management: { ...s.management,
      patrols: s.management.patrols.map((p) => (from >= 31 && p > 0 ? p + 1 : p)), patrolUntil: REEF_ISLANDS.map(() => null) } } : s)) };
  }
  // A v30 or v31 world (M176) takes the coral by kind, the surveys' counts of it and the log's coral entries as M167's
  // v28 did (`withReplayedStocks`): the coral's step changed whole (its heat's curve refit on the community's loss, its
  // other losses with it), so a saved coral's cover is the old step's; unprotected, as M167's (open). A v30 to v32 world
  // (M178) takes the nenue and the surveys' counts of them the same way, and since M183 the kala and theirs (the coral
  // a replay's, grazed by the replay's kala: a coral and its grazers go together), its other stocks the save's (the
  // nenue neither eats nor is eaten); unprotected, as above. A v33 world keeps its coral and its kala, as saved.
  // v33 -> v34 (M179): each management's limits the moano kea's too, Hawaiʻi's rule (none a sanctuary's, there being
  // none before); a v30 to v33 world takes the moano kea and the surveys' counts of it from the replay, every other stock
  // the save's (its hunters' meals apart from their other hunts, it moves no other stock's balance), the v30 to v32
  // world's coral, nenue and kala besides, as above. Unmanaged, as above (open, M179's reviewer: the replay knows no
  // command, so its moano kea was fished at Hawaiʻi's patrols and eaten by jacks under Hawaiʻi's limits, whatever the
  // player had set).
  if (from >= 30 && from <= 33) current = { ...current, sanctuaries: current.sanctuaries.map((s) => (s.management ? { ...s, management: { ...s.management, limits: { ...s.management.limits, goatfish: hawaiiLimits().goatfish } } } : s)) };
  if (from >= 30 && from <= 32) current = withReplayedStocks(current, ['kala', 'nenue', 'moanokea'], true);
  // M191 balanced the moano kea's table again (its share of Maui Nui's goatfish by stratum, its line share by the bag
  // table's own days) and kept version 35: a v34 or v35 world keeps its own moano kea, its counts by age as good under
  // the new table and the player's goatfish limits' work on them kept, settling toward the new balance as it plays, as
  // M183 kept a v33 world's kala (M191's reviewer: replayed, a managed save's moano kea lost its limits' past).
  if (from === 33) current = withReplayedStocks(current, ['moanokea'], false);
  // v34 -> v35 (M187): the hawksbill, the honuʻea, which lives apart from everything else the game steps (neither its food
  // nor its hunters in the game): a v11 to v34 world takes the hawksbills a new game would have on its day, opened with the world
  // and stepped on their own stream to the save's (`withTurtle`), as the tick steps them, every other population the
  // save's; an older world takes the replay's (above).
  if (from >= 11 && from <= 34) current = withTurtle(current, 'honuea');
  // v35 -> v36 (M192): the Deep 7 bottomfish of the deep slope, which live apart from everything else the game steps
  // (neither their food nor their hunters in the game): every older world takes the Deep 7 a new game would have on its
  // day, opened with the world and stepped on their own stream to the save's (`withSlope`), and the surveys' counts of
  // them, every other population the save's.
  if (from <= 35) current = withSlope(current);
  // v36 -> v37 (M193): the deepwater shrimp on the deep slope beside the Deep 7, neither eating nor eaten by anything the
  // game holds: a v36 world takes the shrimp a new game would have on its day, opened with the world and stepped to the
  // save's (`withShrimp`; they draw nothing); an older world takes them with its Deep 7 (`withSlope`).
  if (from === 36) current = withShrimp(current);
  // v37 -> v38 (M197): the tiger sharks, their predation on the honu and the seals in those animals' own measured
  // survival (`sharks.ts`), so neither eating nor eaten by anything the game steps: every older world takes the sharks a
  // new game would have on its day, opened with the world and stepped on their own stream to the save's (`withSharks`),
  // every other population the save's.
  if (from <= 37) current = withSharks(current);
  // v38 -> v39 (M203): each tag and group the drawn animal it was given from, none in an older save (its session's memory
  // of it gone with that session, as it was).
  if (from <= 38) current = { ...current, tags: { animals: current.tags.animals.map((a) => ({ ...a, seenAs: null, seenIn: null })), groups: current.tags.groups.map((g) => ({ ...g, seenAs: null })) } };
  // v39 -> v40 (M218, M220): the taʻape and the manini, which neither eat nor are eaten by anything the game holds: a v30
  // to v39 world takes them and the surveys' counts of them from the replay (`withReplayedStocks`), every other stock and
  // its coral the save's or as above (the manini's grazing joining the coral's from the save's day on, as M183 kept a
  // v33 world's kala), last, so their counts follow every program a current game holds before them. Unmanaged, as above
  // (open).
  // v39 -> v40 (M219): the whitetip reef sharks, neither eating nor eaten by anything the game steps (its reef fish and
  // octopus in their stocks' measured natural deaths): a v38 or v39 world takes the whitetips a new game would have on
  // its day, stepped on their own stream to the save's (`withWhitetips`); an older one takes them with its tiger sharks
  // (`withSharks`).
  if (from === 38 || from === 39) current = withWhitetips(current);
  if (from >= 30 && from <= 39) current = withReplayedStocks(current, ['manini', 'taape'], false);
  // v40 -> v41 (M238): the conservancy's operations, none in an older world: run from its first day as a current game
  // would have run them (`replayedOperations`), last, as a new world holds them, their events after each day's own.
  if (from <= 40) {
    const r = replayedOperations(current);
    current = { ...current, sanctuaries: current.sanctuaries.map((s, i) => ({ ...s, management: r.management[i] ?? null })), log: withDaysLast(current.log, r.events), operations: r.operations, campaign: r.campaign };
  }
  // v41 -> v42 (M239): the campaign, none in an older world: a v40 or older one's replayed with its operations above
  // (it could do nothing the campaign reads); a v41 one's opened on its first day, last, as a new world holds it, its
  // objectives read from the world on its next day (work already done counts), its review on its first year's last day;
  // one saved past that day opened on its own day instead, its review that year's last (review 29: opened on its first,
  // it held a review and paid its grant for every year missed, a day apart; the work behind them is not replayable).
  if (from === 41) {
    const first = openingCampaign({ startEpochDay: current.startEpochDay, day: 0 });
    current = { ...current, campaign: current.startEpochDay + current.day > first.reviewDay ? openingCampaign(current) : first };
  }
  // v42 -> v43 (M274): a v41 or v42 world's operations take an empty plan, no area counted by the conservancy (NOAA's
  // 2024 survey what is known of each, as a new game's) and no heat's choice; each job at sea or in process its own
  // planned day, sent to no island (the whole sanctuary, as every survey was); each finished one started the day it
  // ended, not waiting (its start was not kept: the month's report tells no days for it, review M274's).
  if (from === 41 || from === 42) {
    const o = current.operations;
    current = { ...current, operations: { ...o, plan: [], areaCounts: [], heatChoice: null,
      jobs: o.jobs.map((j) => ({ ...j, island: null, plannedDay: j.startDay })),
      finished: o.finished.map((f) => ({ ...f, startDay: null, waited: 0, island: null })) } };
  }
  // v44 -> v45 (M307): no shore cleaned, so each island's shore holds the opening's reckoning on the world's first day,
  // which is what no stored reckoning means (`shoreReckoning`): nothing to add, as a new world holds none.
  // v43 -> v44 (M277): one boat, nothing built, no volunteers; each job on the *Kilo Kai*; each month's accounts with
  // nothing spent on boats and equipment.
  if (from >= 41 && from <= 43) {
    const o = current.operations;
    const withCapital = (l: Record<string, number>): Ledger => ({ ...l, capital: 0 }) as Ledger;
    current = { ...current, operations: { ...o, boats: 1, projects: [], community: REEF_ISLANDS.map(() => null), jobs: o.jobs.map((j) => ({ ...j, boat: 0 })),
      month: withCapital(o.month), months: o.months.map((m) => ({ ...m, lines: withCapital(m.lines) })) } };
  }

  // Every version: the environment is derived, the sun and the mean year's
  // water for the place and the day, so it is computed again on every load
  // (M19). A save whose places have not moved gets the values it was written
  // with; one written before a version added a field, or at a site since
  // moved, gets what a new game would have on that day; and a damaged one
  // cannot reach the readout with a hole in it.
  const today = epochDayOf(current);
  current.regions = current.regions.map((r) => ({ id: r.id, env: regionEnvironment(r, today), sst: r.sst, plankton: r.plankton }));
  current.sanctuaries = current.sanctuaries.map((s) => ({ ...s, env: sanctuaryEnvironment(s, today) }));
  return current;
}

/** Whether a sanctuary's heat stress is whole: the window's 84 HotSpots and the episode's peak, numbers, none below 0. */
function heatStressWhole(h: unknown): boolean {
  const x = h as Partial<HeatStressState> | undefined;
  return Array.isArray(x?.countedC) && x.countedC.length === DHW_WINDOW_DAYS && x.countedC.every((c) => Number.isFinite(c) && c >= 0)
    && Number.isFinite(x.peakDhw) && (x.peakDhw as number) >= 0;
}

/** Whether a sanctuary's coral is whole: none where its site has no reef; else a cover for every stratum, none below 0, and every depth bin's bleached share and answered heat. */
function coralWhole(c: unknown, hasReef: boolean, from: number): boolean {
  if (!hasReef) return c === null;
  const x = c as Partial<CoralState> | null | undefined;
  const byBin = (v: unknown, whole: (y: number) => boolean): boolean =>
    Array.isArray(v) && v.length === DEPTH_BINS.length && v.every((y) => Number.isFinite(y) && whole(y as number));
  const strata = REEF_ISLANDS.length * DEPTH_BINS.length;
  return Array.isArray(x?.coverPercent) && x.coverPercent.length === strata
    && x.coverPercent.every((v) => Number.isFinite(v) && v >= 0)
    // A share in percent: 0 to 100 (a definition).
    && byBin(x.bleachedPercent, (y) => y >= 0 && y <= 100)
    && byBin(x.answeredDhw, (y) => y >= 0)
    // From v29 (M167), the bottom beside it: each stratum's turf and macroalgae, none below 0, the stratum's sediment,
    // coral, turf and macroalgae not past its whole bottom; and nothing else.
    && (from < 29 || (Object.keys(x as object).length === (from < 32 ? 4 : 5) && bottomWhole(x?.bottom, x?.coverPercent as number[])))
    // From v32 (M176), its kinds: each stratum's five, none below 0, their sum its cover.
    && (from < 32 || kindsWhole(x?.kindPercent, x?.coverPercent as number[]));
}

/** Whether a reef's coral by kind is whole (M176): each stratum's every kind a number not below 0, their sum its cover. */
function kindsWhole(k: unknown, cover: readonly number[]): boolean {
  return Array.isArray(k) && k.length === cover.length && k.every((row, i) => Array.isArray(row) && row.length === CORAL_KINDS.length
    && row.every((y) => Number.isFinite(y) && (y as number) >= 0)
    // To the step's rounding: each kind and their sum held to six places (`held`).
    && Math.abs((row as number[]).reduce((a, b) => a + b, 0) - (cover[i] as number)) <= 1e-5);
}

/** Whether a reef's bottom is whole (M167): each stratum's turf and macroalgae a number not below 0, its sediment, coral, turf and macroalgae within its bottom. */
function bottomWhole(b: unknown, cover: readonly number[]): boolean {
  const x = b as Partial<CoralBottom> | null | undefined;
  const each = (v: unknown): v is number[] => Array.isArray(v) && v.length === REEF_BOTTOM.length && v.every((y) => Number.isFinite(y) && (y as number) >= 0);
  if (!x || Object.keys(x).length !== 2 || !each(x.turfPercent) || !each(x.macroalgaePercent)) return false;
  const { turfPercent, macroalgaePercent } = x;
  // To a millionth of a point past the whole: the step's rounding (`held`, six places).
  return cover.every((c, i) => (REEF_BOTTOM[i]?.sedimentPercent ?? 0) + c + (turfPercent[i] as number) + (macroalgaePercent[i] as number) <= 100 + 1e-6);
}

/** Whether one species' stocks are whole: every island's every age class a whole count not below 0, and the larvae afloat a number not below 0 for every island in every slot. */
function stockWhole(f: unknown, life: FishLife): boolean {
  const x = f as Partial<FishState> | null | undefined;
  const rows = (v: unknown, length: number, width: number, whole: (y: number) => boolean): boolean =>
    Array.isArray(v) && v.length === length && v.every((r) => Array.isArray(r) && r.length === width && r.every((y) => Number.isFinite(y) && whole(y as number)));
  // Its fields counted too, as every population's are (review M116's test-breaker: a stray field loaded).
  return x !== null && x !== undefined && Object.keys(x).length === 2 && rows(x.islands, FISH_ISLANDS.length, ageClasses(life), (y) => Number.isInteger(y) && y >= 0)
    && rows(x.afloat, life.larvalDays, FISH_ISLANDS.length, (y) => y >= 0);
}
/** Whether a sanctuary's reef fish are whole: none where its site has none; else each of the version's species' stocks, and no other. */
function fishWhole(f: unknown, hasStock: boolean, lives: readonly FishLife[]): boolean {
  if (!hasStock) return f === null;
  const x = f as Partial<ReefFish> | null | undefined;
  return x !== null && x !== undefined && Object.keys(x).length === lives.length && lives.every((life) => stockWhole(x[life.id], life));
}

/** The causes a kind's tagged animal can die of (review M148: a honu dead "crowded" loaded): a honu's its stage's chance, a seal's or a humpback's that or its age, a reef stock's its class's causes and its age where every one dies at its oldest. */
function tagCauses(species: unknown): readonly string[] {
  if (species === 'honu' || species === 'honuea') return ['natural'];
  if (species === 'monkSeal' || species === 'humpback' || species === 'tigerShark') return ['natural', 'old age'];
  const life = FISH_LIVES.find((l) => l.id === species);
  return life?.diesAtOldest === true ? ['natural', 'fished', 'eaten', 'crowded', 'old age'] : ['natural', 'fished', 'eaten', 'crowded'];
}

/** A tagged animal's fields and a group's, as `tagAnimal` and `tagGroup` give them (by their types' definition). */
const ANIMAL_FIELDS = 14;
const GROUP_FIELDS = 8;

/** Whether a sanctuary's management is whole (M165): its herbivore protection a boolean for each of the reef's islands, from v30 (M170) each fish group's limits an option it has, from v31 (M171) each island's patrols a level, and only where it has reef fish. */
function managementWhole(m: unknown, hasFish: boolean, from: number, today: number): boolean {
  if (!hasFish) return m === null;
  const x = m as Partial<Management> | null;
  if (x === null || typeof x !== 'object' || Object.keys(x).length !== (from >= 43 ? 4 : from >= 31 ? 3 : from >= 30 ? 2 : 1) || !Array.isArray(x.herbivoreProtection)
    || x.herbivoreProtection.length !== REEF_ISLANDS.length || !x.herbivoreProtection.every((p) => typeof p === 'boolean')) return false;
  if (from < 30) return true;
  const limits = x.limits as Record<string, { bag?: unknown; size?: unknown }> | undefined;
  const option = (n: unknown, of: readonly unknown[]): boolean => Number.isInteger(n) && (n as number) >= 0 && (n as number) < of.length;
  // The levels a version had: three before the one-stretch level (M274).
  if (from >= 31 && !(Array.isArray(x.patrols) && x.patrols.length === REEF_ISLANDS.length && x.patrols.every((p) => option(p, from >= 43 ? PATROL_LEVELS : PATROL_LEVELS.slice(0, 3))))) return false;
  // Each island's term a day still to come, only beside a patrol bought (M274; review M274's: a day passed never ended it).
  if (from >= 43 && !(Array.isArray(x.patrolUntil) && x.patrolUntil.length === REEF_ISLANDS.length
    && x.patrolUntil.every((u, i) => u === null || (Number.isInteger(u) && u > today && (x.patrols?.[i] ?? 0) > 0)))) return false;
  // The moano kea's limits from v34 (M179).
  const groups = from >= 34 ? LIMIT_GROUPS : LIMIT_GROUPS.filter((g) => g !== 'goatfish');
  return limits !== null && typeof limits === 'object' && Object.keys(limits).length === groups.length
    && groups.every((g) => { const l = limits[g]; return l !== null && typeof l === 'object' && Object.keys(l).length === 2
      && option(l.bag, LIMIT_OPTIONS[g].bags) && option(l.size, LIMIT_OPTIONS[g].sizes); });
}

/** Whether the campaign is whole (M239), as `campaign.ts` keeps it. */
function campaignWhole(c: unknown, today: number): boolean {
  const x = c as Partial<Campaign> | null;
  // The objectives the campaign asks, and those it asked before M262, which a save may hold done.
  const ids = new Set([...CHAPTERS.flatMap((ch) => OBJECTIVES[ch].map((o) => o.id)), ...RETIRED_OBJECTIVES]);
  // Every day it holds one already come, and its next review one still to come (review 29: a review day passed held a
  // review, and paid its grant, every day; a day to come counted as done).
  const day = (d: unknown): boolean => Number.isInteger(d) && (d as number) <= today;
  if (!x || typeof x !== 'object' || !Number.isInteger(x.chapter) || (x.chapter as number) < 0 || (x.chapter as number) > CHAPTERS.length || !Number.isInteger(x.reviewDay) || (x.reviewDay as number) < today) return false;
  if (!Array.isArray(x.chapterDays) || x.chapterDays.length !== Math.min(x.chapter as number, CHAPTERS.length - 1) + 1 || !x.chapterDays.every(day)) return false;
  if (!x.done || typeof x.done !== 'object' || !Object.entries(x.done).every(([k, v]) => ids.has(k) && day(v))) return false;
  if (!x.marks || typeof x.marks !== 'object' || !Object.entries(x.marks).every(([k, v]) => (MARKS as readonly string[]).includes(k) && day(v))) return false;
  if (!(x.test === null || x.test === 'heatwave' || x.test === 'season')) return false;
  // Each review whole (M240: one a year).
  return Array.isArray(x.reviews) && x.reviews.every((r) => !!r && typeof r === 'object' && day(r.day) && Number.isInteger(r.award) && Array.isArray(r.lines) && r.lines.length === REVIEW_LINES.length
    && r.lines.every((l, i) => l?.id === REVIEW_LINES[i] && typeof l.label === 'string' && typeof l.text === 'string' && ['good', 'fair', 'poor'].includes(l.grade)));
}

/** Whether the operations are whole (M238), as `operations.ts` keeps them. */
function operationsWhole(o: unknown, today: number, from: number): boolean {
  const x = o as Partial<Operations> | null;
  if (!x || typeof x !== 'object' || !Number.isInteger(x.funds) || !Number.isInteger(x.nextStaffId) || !Number.isInteger(x.nextJobId) || !Number.isInteger(x.hired)) return false;
  if (from >= 43 && !planWhole(x, today, from)) return false;
  if (from >= 44 && !buildWhole(x, today)) return false;
  // The boats a version could run: one before v44 (M277).
  const boats = from >= 44 ? (x.boats as number) : 1;
  // The survey kinds a version had: the bleaching assessment from v43 (M274), the shore cleanup from v45 (M307).
  const kinds = Object.keys(SURVEY_NAMES).filter((k) => (from >= 43 || k !== 'reefBleaching') && (from >= 45 || k !== 'shoreCleanup'));
  if (!shoresWhole(x, today, from)) return false;
  // A cover the tourism is reckoned against one there is (review 29: a negative one booked negative income).
  if (!(x.tourismCover === null || (Number.isFinite(x.tourismCover) && (x.tourismCover as number) > 0))) return false;
  // The ledger's lines a version had: boats and equipment from v44 (M277).
  const lines = LEDGER_LINES.filter((k) => from >= 44 || k !== 'capital');
  const ledger = (l: unknown): boolean => !!l && typeof l === 'object' && Object.keys(l).length === lines.length
    && lines.every((k) => Number.isInteger((l as Record<string, unknown>)[k]));
  if (!ledger(x.month) || !Array.isArray(x.months) || !x.months.every((m) => Number.isInteger(m?.year) && Number.isInteger(m?.month) && m.month >= 1 && m.month <= 12 && Number.isInteger(m?.funds) && ledger(m?.lines))) return false;
  if (!Array.isArray(x.staff) || !x.staff.every((m) => Number.isInteger(m?.id) && m.id < (x.nextStaffId as number) && typeof m?.name === 'string' && Object.hasOwn(ECONOMY.mostOf, m.role) && Number.isInteger(m.paidTo))) return false;
  // Each member once, and no role past the most the conservancy may hold (review 29: six liaisons loaded).
  const staff = new Set(x.staff.map((m) => m.id));
  // A captain for each boat run or on order from v44 (M277).
  const most = (r: keyof typeof ECONOMY.mostOf): number => (from >= 44 && r === 'captain' ? mostOf(x as Operations, r) : ECONOMY.mostOf[r]);
  if (staff.size !== x.staff.length || (Object.keys(ECONOMY.mostOf) as (keyof typeof ECONOMY.mostOf)[]).some((r) => x.staff!.filter((m) => m.role === r).length > most(r))) return false;
  if (!Array.isArray(x.finished) || !x.finished.every((f) => Number.isInteger(f?.id) && (f.kind === 'survey' || f.kind === 'rule') && Number.isInteger(f.endDay) && f.endDay <= today && typeof f.title === 'string'
    && (from < 43 || ((f.startDay === null || (Number.isInteger(f.startDay) && f.startDay <= f.endDay)) && Number.isInteger(f.waited) && f.waited >= 0 && islandOrNone(f.island)))
    // A cleanup's tonnes from v45 (M307): a trip's to an island, a finite tonnage not below 0; none on anything else.
    && (f.tonnes === undefined || (from >= 45 && f.kind === 'survey' && f.island !== null && typeof f.tonnes === 'number' && Number.isFinite(f.tonnes) && f.tonnes >= 0)))) return false;
  if (!Array.isArray(x.jobs)) return false;
  // Each job's next phase still to come and its phases in order (review 29: a phase ended yesterday never ended, the
  // boat at sea and its crew at work for good), one boat at sea, and no member on two jobs.
  const busy = x.jobs.flatMap((j) => (Array.isArray(j?.staff) ? j.staff : []));
  // One survey a boat, each on a boat of its own (M277).
  const out = x.jobs.filter((j) => j?.kind === 'survey');
  if (new Set(busy).size !== busy.length || out.length > boats || (from >= 44 && new Set(out.map((j) => j.boat)).size !== out.length)) return false;
  return x.jobs.every((j) => Number.isInteger(j?.id) && j.id < (x.nextJobId as number) && Number.isInteger(j.startDay) && j.startDay <= today && Number.isInteger(j.cost)
    && Array.isArray(j.staff) && j.staff.every((id) => staff.has(id)) && Array.isArray(j.phaseEnds) && j.phaseEnds.every(Number.isInteger)
    && j.phaseEnds.every((d, i) => i === 0 || d > (j.phaseEnds[i - 1] as number)) && j.phaseEnds.some((d) => d > today)
    && (from < 43 || (Number.isInteger(j.plannedDay) && j.plannedDay <= j.startDay && islandOrNone(j.island) && (j.island === null || j.kind === 'community' || (j.kind === 'survey' && islandSurvey(j.survey as SurveyKind)))))
    && (from < 44 || (Number.isInteger(j.boat) && j.boat >= 0 && j.boat < boats && (j.kind === 'survey' || j.boat === 0)))
    // A liaison's setting up of an island's volunteers from v44 (M277).
    && (j.kind === 'community'
      ? from >= 44 && j.phaseEnds.length === 1 && j.survey === null && j.days === null && j.rule === null && j.island !== null && j.staff.length === 1
        // Its liaison, for an island whose volunteers do not yet count, once (review M277's).
        && x.staff!.find((m) => m.id === j.staff[0])?.role === 'liaison' && (x.community as (number | null)[])[REEF_ISLANDS.indexOf(j.island)] === null
        && x.jobs!.filter((o) => o?.kind === 'community' && o.island === j.island).length === 1
      : j.kind === 'survey'
      ? j.phaseEnds.length === 1 && kinds.includes(j.survey as string) && Number.isInteger(j.days) && j.rule === null && (j.survey !== 'shoreCleanup' || j.island !== null)
      // A rule's liaison free once its consultation is done.
      : j.kind === 'rule' && j.phaseEnds.length === 2 && j.survey === null && j.days === null && typeof checkedRule(j.rule) !== 'string' && (today < (j.phaseEnds[0] as number) || j.staff.length === 0)));
}

/**
 * Whether the boats, the projects and the volunteers are whole (M277): one boat or two, two only once its order has
 * come; at most one boat ordered, ordered before it is ready, on a day come; each island's volunteers counting from a
 * day come, or none.
 */
function buildWhole(x: Partial<Operations>, today: number): boolean {
  if (!(x.boats === 1 || x.boats === 2) || !Array.isArray(x.projects) || x.projects.length > 1 || !Array.isArray(x.community) || x.community.length !== REEF_ISLANDS.length) return false;
  if (!x.projects.every((p) => p?.kind === 'boat' && Number.isInteger(p.id) && p.id < (x.nextJobId as number) && Number.isInteger(p.orderedDay) && p.orderedDay <= today
    && Number.isInteger(p.readyDay) && p.readyDay > p.orderedDay && Number.isInteger(p.cost) && p.cost >= 0)) return false;
  const ready = x.projects.some((p) => p.readyDay <= today);
  if ((x.boats === 2) !== ready) return false;
  return x.community.every((d) => d === null || (Number.isInteger(d) && d <= today));
}

/**
 * Whether the shores' reckonings are whole (M307): none before v45; from it none at all (no shore cleaned), or one for
 * each of some of the reef's islands, its tonnes finite and not below 0, its day an epoch day come.
 */
function shoresWhole(x: Partial<Operations>, today: number, from: number): boolean {
  if (x.shores === undefined) return true;
  if (from < 45 || !x.shores || typeof x.shores !== 'object' || Array.isArray(x.shores)) return false;
  return Object.entries(x.shores).every(([island, k]) => (REEF_ISLANDS as readonly string[]).includes(island) && !!k && typeof k === 'object'
    && typeof k.tonnes === 'number' && Number.isFinite(k.tonnes) && k.tonnes >= 0 && Number.isInteger(k.day) && k.day <= today);
}

/** Whether a value is one of the reef's islands, or none. */
const islandOrNone = (i: unknown): boolean => i === null || (REEF_ISLANDS as readonly unknown[]).includes(i);

/**
 * Whether the boat's plan, the areas' counts and the heat's choice are whole (M274), as `operations.ts` keeps them: each
 * plan's id below the next and none twice or beside a job's, its kind, island, days and biologists a plan's; each count
 * an island's of a kind, its day come, its three depths' (one for the herbivores) sites, values and errors.
 */
function planWhole(x: Partial<Operations>, today: number, from: number): boolean {
  if (!Array.isArray(x.plan) || x.plan.length > ECONOMY.planLength || !Array.isArray(x.areaCounts)) return false;
  // The surveys' counts and each island's volunteers' kept apart (M277).
  if (x.areaCounts.filter((c) => c?.by !== 'community').length > AREA_COUNTS_KEPT
    || REEF_ISLANDS.some((island) => x.areaCounts!.filter((c) => c?.by === 'community' && c.island === island).length > COMMUNITY_COUNTS_KEPT)) return false;
  if (!(x.heatChoice === null || x.heatChoice === 'assess' || x.heatChoice === 'keep')) return false;
  // No id twice among the plan, the jobs and (from v44, M277) the projects.
  const ids = [...x.plan.map((p) => p?.id), ...(Array.isArray(x.jobs) ? x.jobs.map((j) => j?.id) : []), ...(from >= 44 && Array.isArray(x.projects) ? x.projects.map((p) => p?.id) : [])];
  if (new Set(ids).size !== ids.length) return false;
  if (!x.plan.every((p) => Number.isInteger(p?.id) && p.id < (x.nextJobId as number) && (p.kind as string) in SURVEY_NAMES && islandOrNone(p.island)
    && (p.island === null || islandSurvey(p.kind))
    // A shore cleanup from v45 (M307), to an island.
    && (p.kind !== 'shoreCleanup' || (from >= 45 && p.island !== null)) && ECONOMY.surveyDays.includes(p.days) && Number.isInteger(p.biologists)
    // An assessment no longer than its cap, every path that plans one capped since M274 (review 32's: one longer held the plan for good).
    && (p.kind !== 'reefBleaching' || p.days <= ECONOMY.assessmentDays)
    // Its biologists a crew its kind takes (review M274's: three divers loaded, and the boat waited on them for good).
    && (p.kind === 'slopeCamera' ? p.biologists >= 1 : p.biologists >= 2 && p.biologists % 2 === 0)
    && p.biologists <= ECONOMY.mostOf.biologist && Number.isInteger(p.plannedDay) && p.plannedDay <= today)) return false;
  const finite = (v: unknown): boolean => v === null || (typeof v === 'number' && Number.isFinite(v) && v >= 0);
  return x.areaCounts.every((c) => !!c && typeof c === 'object' && Number.isInteger(c.day) && c.day <= today && (REEF_ISLANDS as readonly string[]).includes(c.island)
    && (c.kind === 'cover' || c.kind === 'herbivores' || c.kind === 'bleached') && (c.by === 'noaa' || c.by === 'conservancy' || (from >= 44 && c.by === 'community'))
    && [c.sites, c.value, c.se].every((a) => Array.isArray(a) && a.length === (c.kind === 'herbivores' ? 1 : DEPTH_BINS.length))
    && c.sites.every((n) => Number.isInteger(n) && n >= 0) && c.value.every(finite) && c.se.every(finite));
}

/** Whether the monitoring programs' results are whole (M160): only programs the game runs, each survey as `stepMonitoring` keeps it. */
function monitoringWhole(m: unknown, today: number, from: number): boolean {
  if (m === null || typeof m !== 'object' || Array.isArray(m)) return false;
  // A survey's three fields by its type's definition, the save versions counters (no rates). Each program's counts in a
  // version that had it only (review M190: a v34 save's hawksbill counts loaded and were kept,
  // the migration's own taken for the save's).
  return Object.entries(m as Record<string, unknown>).every(([id, list]) => PROGRAMS[id]?.schedule !== undefined && from >= (PROGRAM_SINCE[id] ?? 27) && Array.isArray(list)
    && list.every((r: SurveyResult, k) => r !== null && typeof r === 'object' && Object.keys(r).length === 3 && Number.isInteger(r.day) && r.day <= today
      && (k === 0 || r.day > (list[k - 1] as SurveyResult).day) && Number.isFinite(r.value) && r.value >= 0 && (r.cv === null || (Number.isFinite(r.cv) && r.cv > 0))));
}

/** The save version each program's counts arrived in (counters, by definition), where it is later than v27's monitoring (M178, M179, M187, M192). */
const PROGRAM_SINCE: Readonly<Record<string, number>> = { nenue: 33, moanokea: 34, honuea: 35, ...Object.fromEntries(DEEP7_SPECIES.map((d) => [d.id, 36])), taape: 40, manini: 40 };

/** A world from the same seed, site and first day as a save's, stepped to its day: what the replays take (M176: shared). */
function replayOf(world: World): World {
  const fresh = createWorld({ seed: world.seed, opening: world.sanctuaries[0]?.siteId, startDate: civilFromEpochDay(world.startEpochDay) });
  tickMany(fresh, world.day);
  return fresh;
}

/**
 * A v29 or older world with what a new game would have on its day: the surveys (v26 or older, M160), the kala (v25 to
 * v27, M165), the coral with its bottom and the surveys' counts of it (M167), and every reef stock and every survey's
 * counts under Hawaiʻi's rules since its opening (M170).
 */
function withReplayed(world: World, from: number): World {
  const fresh = replayOf(world);
  const monitoring = fresh.monitoring;
  const sanctuaries = world.sanctuaries.map((s, k) => {
    const same = fresh.sanctuaries[k]?.siteId === s.siteId ? fresh.sanctuaries[k] : undefined;
    const withFish = s.fish && same?.fish ? { ...s, fish: same.fish } : s;
    return withFish.coral && same?.coral ? { ...withFish, coral: same.coral } : withFish;
  });
  // The log's coral entries the replay's too (M167's reviewer: kept, their figures were the old coral's): the replay's
  // log in the tick's order, the save's own entries (what the player did) after each day's, as the older replays'.
  const log = withReplayedLog(world.log.filter((e) => !V8_CORAL_TITLE.test(e.title)), fresh.log);
  return { ...world, monitoring, sanctuaries, log };
}

/**
 * A world's stocks named, and with `coral` its coral, as a current game would have them (M176): each sanctuary's stocks
 * and coral, their surveys and the log's coral entries the replay's from the same seed and first day (`withReplayed`'s,
 * its other fish and their surveys the save's). A v30 to v32 world's coral by kind (M176), its nenue (M178), its kala
 * (M183: its table balanced again on Guam's weights, a replay's kala the new table's; a v33 world keeps its own kala,
 * its counts by age as good under the new weights, and its coral, the player's protection with them: M183's reviewer)
 * and its moano kea (M179); a v33 world's moano kea alone.
 */
function withReplayedStocks(world: World, stocks: readonly FishSpeciesId[], coral: boolean): World {
  const fresh = replayOf(world);
  const sanctuaries = world.sanctuaries.map((s, k) => {
    const same = fresh.sanctuaries[k]?.siteId === s.siteId ? fresh.sanctuaries[k] : undefined;
    // The stocks in the order a new world holds them, and the coral their grazing moves.
    const fish = same?.fish;
    const withStocks = s.fish && fish ? { ...s, fish: { ...s.fish, ...Object.fromEntries(stocks.map((id) => [id, fish[id]])) } } : s;
    return coral && withStocks.coral && same?.coral ? { ...withStocks, coral: same.coral } : withStocks;
  });
  // The coral's and the stocks' surveys the replay's, every other the save's, in the order a current game holds them
  // (M178's reviewer: the honu's, first counted on 1 August, went before the nenue's in a save made after it).
  const replayedPrograms: readonly string[] = [...(coral ? ['coral'] : []), ...stocks];
  const keys = [...Object.keys(fresh.monitoring), ...Object.keys(world.monitoring).filter((key) => !(key in fresh.monitoring))];
  const monitoring = Object.fromEntries(keys.flatMap((key) => {
    const from = replayedPrograms.includes(key) && fresh.monitoring[key] ? fresh.monitoring : world.monitoring;
    return from[key] ? [[key, from[key]]] : [];
  })) as World['monitoring'];
  // The log in the replay's order (M176's reviewer: the replay's coral entries alone went before a day's other entries,
  // an episode's end and its deaths swapped): the replay's coral entries and the entries the save shares with it, then
  // the save's own after each day's (`withReplayedLog`).
  const saved = world.log.filter((e) => !V8_CORAL_TITLE.test(e.title));
  const shared = new Map<string, number>();
  for (const e of saved) shared.set(JSON.stringify(e), (shared.get(JSON.stringify(e)) ?? 0) + 1);
  const replayed = fresh.log.filter((e) => {
    if (V8_CORAL_TITLE.test(e.title)) return true;
    const n = shared.get(JSON.stringify(e)) ?? 0;
    if (n > 0) shared.set(JSON.stringify(e), n - 1);
    return n > 0;
  });
  return coral ? { ...world, monitoring, sanctuaries, log: withReplayedLog(saved, replayed) } : { ...world, monitoring, sanctuaries };
}

/** A save's tags whole (M148): every tagged animal and group as a tagging leaves it, on a population the world holds. */
function tagsWhole(t: unknown, world: World, from: number): boolean {
  const x = t as Partial<Tags> | null | undefined;
  if (!x || !Array.isArray(x.animals) || !Array.isArray(x.groups)) return false;
  const whole = (n: unknown): boolean => Number.isInteger(n);
  // The classes a kind's population has at a site, or none where it holds none (a reef stock's by its island).
  const classesAt = (siteId: unknown, species: unknown, island: unknown): number | null => {
    const s = world.sanctuaries.find((q) => q.siteId === siteId);
    if (!s) return null;
    if (species === 'honu') return island === null && s.turtles ? s.turtles.honu.home.length : null;
    // The hawksbill and the tiger shark tagged from v39 (M203).
    if (species === 'honuea') return from >= 39 && island === null && s.turtles ? s.turtles.honuea.home.length : null;
    if (species === 'tigerShark') return from >= 39 && island === null && s.sharks ? s.sharks.tiger.counts.length : null;
    if (species === 'monkSeal') return island === null && s.seals ? s.seals.monk.counts.length : null;
    if (species === 'humpback') return island === null && s.whales ? s.whales.humpback.counts.length : null;
    const life = FISH_LIVES.find((l) => l.id === species);
    // The save's own stock (review M190: a tagged moano kea loaded in a v33 save, which held none).
    return s.fish && life && (s.fish as unknown as Record<string, unknown>)[life.id] !== undefined && whole(island) && (island as number) >= 0 && (island as number) < FISH_ISLANDS.length ? ageClasses(life) : null;
  };
  const named = (n: unknown): boolean => typeof n === 'string' && nameKept(n);
  // Its fields a tagging's, and from v39 the drawn animal it was given from, a whole number or none, and an animal's
  // habitat it was tagged in, one a tag tells or none (M203).
  const [animalFields, groupFields] = from >= 39 ? [ANIMAL_FIELDS, GROUP_FIELDS] : [ANIMAL_FIELDS - 2, GROUP_FIELDS - 1];
  const seen = (x: { seenAs?: unknown }): boolean => from < 39 || x.seenAs === null || whole(x.seenAs);
  const seenIn = (x: { seenIn?: unknown }): boolean => from < 39 || x.seenIn === null || TAG_HABITATS.includes(x.seenIn as TagHabitat);
  // Each id the world's own, given once and before its next (review M148: an id a sanctuary's, or one given twice, loaded);
  // and nothing tagged or dead after today.
  const today = epochDayOf(world);
  const ids = [...x.animals, ...x.groups].map((t) => (t as { id?: unknown } | null)?.id);
  if (!ids.every((id) => whole(id) && (id as number) < world.nextEntityId && !world.sanctuaries.some((s) => s.id === id)) || new Set(ids).size !== ids.length) return false;
  const past = (d: unknown): boolean => whole(d) && (d as number) <= today;
  const pods = new Map(x.groups.map((g) => [(g as TaggedGroup | null)?.id, g as TaggedGroup]));
  return x.animals.every((a: TaggedAnimal) => {
    const n = a ? classesAt(a.siteId, a.species, a.island) : null;
    const g = a?.groupId === null ? null : pods.get(a?.groupId);
    // Its fields a tagging's, no more (review M149: a stray field loaded and was carried on); away only a honu or a whale.
    const turtle = a?.species === 'honu' || a?.species === 'honuea';
    return n !== null && Object.keys(a).length === animalFields && seen(a) && seenIn(a) && (a.away === false || turtle || a.species === 'humpback') && whole(a.id) && named(a.name) && whole(a.cls) && a.cls >= 0 && a.cls < n && past(a.taggedOn) && typeof a.away === 'boolean'
      && (a.female === null ? !turtle : typeof a.female === 'boolean' && turtle)
      && (a.quantile === null ? a.species !== 'humpback' : typeof a.quantile === 'number' && a.quantile >= 0 && a.quantile < 1 && a.species === 'humpback')
      && (a.died === null || (typeof a.died === 'object' && past(a.died.on) && a.died.on >= a.taggedOn && tagCauses(a.species).includes(a.died.cause)))
      && (g === null || (g !== undefined && g.kind === 'pod' && g.species === a.species && g.siteId === a.siteId));
  }) && Object.keys(x).length === 2 && x.groups.every((g: TaggedGroup) => g && Object.keys(g).length === groupFields && seen(g) && named(g.name) && past(g.taggedOn)
    && (g.kind === 'school' ? classesAt(g.siteId, g.species, g.island) !== null && g.island !== null : g.kind === 'pod' && g.island === null && classesAt(g.siteId, g.species, FISH_LIVES.some((l) => l.id === g.species) ? 0 : null) !== null));
}

/** Whether a sanctuary's sea turtles are whole: none where its site has none; else the honu, and from v35 the hawksbill after it (M187), each every class a whole count not below 0 in each place, and the hatchlings due a number not below 0. */
function turtlesWhole(t: unknown, hasTurtles: boolean, from: number): boolean {
  if (!hasTurtles) return t === null;
  const x = t as Record<string, Partial<TurtleState> | undefined> | null | undefined;
  const kinds = TURTLES.filter((k) => k.key === 'honu' || from >= 35);
  const whole = (life: (typeof TURTLES)[number]['life'], y: Partial<TurtleState> | undefined): boolean => {
    const counts = (v: unknown): boolean => Array.isArray(v) && v.length === turtleClasses(life) && v.every((n) => Number.isInteger(n) && (n as number) >= 0);
    // Its fields counted too, as every population's are (review M116: a stray field loaded, carried into every later
    // save), each turtle's five by the definition of `TurtleState`.
    return y !== undefined && y !== null && Object.keys(y).length === 5 && counts(y.atSea) && counts(y.home) && counts(y.awayFemales)
      && counts(y.awayMales) && Number.isFinite(y.hatchlings) && (y.hatchlings as number) >= 0;
  };
  return x !== null && x !== undefined && typeof x === 'object' && Object.keys(x).join() === kinds.map((k) => k.key).join() && kinds.every((k) => whole(k.life, x[k.key]));
}

/**
 * A world with one turtle a new game would have on its day, after any it holds: the honu in a v11 to v14 world (M93),
 * the hawksbill in a v11 to v34 one (M187); opened with the world and stepped on its own stream to the save's day. A
 * world with surveys (v27 on) takes its program's counts on the way, as the tick takes them after the animals' stage,
 * after every program the save holds (its first count, on 1 August, the same day as the honu's, which is counted
 * before it), unless it holds them already (a v29 or older world's surveys are a replay's, `withReplayed`).
 */
function withTurtle(world: World, key: (typeof TURTLES)[number]['key']): World {
  const t = TURTLES.find((k) => k.key === key) as (typeof TURTLES)[number];
  const counted = world.monitoring && !(key in world.monitoring) ? [] as SurveyResult[] : null;
  const sanctuaries = world.sanctuaries.map((s) => {
    if (!siteHasTurtles(s.siteId)) return { ...s, turtles: null };
    const x = openingTurtles(world.startEpochDay)[key];
    const turtles = { ...s.turtles, [key]: x } as SeaTurtles;
    for (let d = 1; d <= world.day; d++) {
      stepHonu(t.life, x, world.startEpochDay + d, t.supply, turtleDraws({ seed: world.seed, day: d }, s.siteId, undefined, t.salt));
      const r = counted && surveyOn({ ...world, day: d, sanctuaries: [{ ...s, turtles }] }, key);
      if (r) counted?.push(r);
    }
    return { ...s, turtles };
  });
  return counted && counted.length > 0 ? { ...world, sanctuaries, monitoring: { ...world.monitoring, [key]: counted } } : { ...world, sanctuaries };
}

/**
 * Whether a sanctuary's deep slope is whole (M192): none where its site has none; else the Deep 7, its year's starting
 * biomass a finite number above 0, and from v37 the shrimp after it (M193), each sex's classes every one a finite number
 * not below 0; and nothing else.
 */
function slopeWhole(x: unknown, hasSlope: boolean, from: number): boolean {
  if (!hasSlope) return x === null;
  const y = x as { deep7?: { yearStartKg?: unknown }; shrimp?: { females?: unknown; males?: unknown } } | null | undefined;
  const classes = (v: unknown): boolean => Array.isArray(v) && v.length === SHRIMP.oldestMonths + 1 && v.every((n) => Number.isFinite(n) && (n as number) >= 0);
  const shrimpWhole = (s: { females?: unknown; males?: unknown } | undefined): boolean =>
    s !== null && s !== undefined && typeof s === 'object' && Object.keys(s).join() === 'females,males' && classes(s.females) && classes(s.males);
  return y !== null && y !== undefined && typeof y === 'object' && Object.keys(y).join() === (from >= 37 ? 'deep7,shrimp' : 'deep7') && y.deep7 !== null && typeof y.deep7 === 'object'
    && Object.keys(y.deep7).join() === 'yearStartKg' && Number.isFinite(y.deep7.yearStartKg) && (y.deep7.yearStartKg as number) > 0
    && (from < 37 || shrimpWhole(y.shrimp));
}

/** Whether a sanctuary's sharks are whole (M197): none where its site has none; else the tiger sharks and, from v40, the whitetips (M219), every class a whole count not below 0, and nothing else. */
function sharksWhole(x: unknown, hasSharks: boolean, from: number): boolean {
  if (!hasSharks) return x === null;
  const y = x as Record<string, unknown> | null | undefined;
  const whole = (z: unknown, classes: number): boolean => {
    const c = z as { counts?: unknown } | null | undefined;
    return c !== null && c !== undefined && typeof c === 'object' && Object.keys(c).join() === 'counts' && Array.isArray(c.counts) && c.counts.length === classes
      && c.counts.every((n) => Number.isInteger(n) && (n as number) >= 0);
  };
  return y !== null && y !== undefined && typeof y === 'object' && Object.keys(y).join() === (from >= 40 ? 'tiger,whitetip' : 'tiger')
    && whole(y.tiger, sharkClasses(TIGER_SHARK)) && (from < 40 || whole(y.whitetip, sharkClasses(WHITETIP_SHARK)));
}

/** A v37 or older world with the tiger sharks a new game would have on its day (M197): opened with the world and stepped on their own stream to the save's day, after its deep slope (before its management, where a new world holds them). */
function withSharks(world: World): World {
  return {
    ...world,
    sanctuaries: world.sanctuaries.map((s) => {
      const { management, ...before } = s;
      if (!siteHasTigerSharks(s.siteId)) return { ...before, sharks: null, management };
      const sharks = openingSharks(world.startEpochDay);
      for (let d = 1; d <= world.day; d++) {
        stepTigerSharks(TIGER_SHARK, sharks.tiger, world.startEpochDay + d, sharkDraws({ seed: world.seed, day: d }, s.siteId));
        stepTigerSharks(WHITETIP_SHARK, sharks.whitetip, world.startEpochDay + d, whitetipDraws({ seed: world.seed, day: d }, s.siteId));
      }
      return { ...before, sharks, management };
    }),
  };
}

/** A v38 or v39 world with the whitetips a new game would have on its day (M219): opened with the world and stepped on their own stream to the save's day, beside its tiger sharks. */
function withWhitetips(world: World): World {
  return {
    ...world,
    sanctuaries: world.sanctuaries.map((s) => {
      if (!s.sharks) return s;
      const whitetip = openingWhitetips(world.startEpochDay);
      for (let d = 1; d <= world.day; d++) stepTigerSharks(WHITETIP_SHARK, whitetip, world.startEpochDay + d, whitetipDraws({ seed: world.seed, day: d }, s.siteId));
      return { ...s, sharks: { ...s.sharks, whitetip } };
    }),
  };
}

/** A v36 world with the deepwater shrimp a new game would have on its day (M193): opened with the world and stepped to the save's day, after its Deep 7. */
function withShrimp(world: World): World {
  return {
    ...world,
    sanctuaries: world.sanctuaries.map((s) => {
      if (!s.slope) return s;
      const shrimp = openingShrimp(world.startEpochDay);
      for (let d = 1; d <= world.day; d++) stepShrimpDay(shrimp, world.startEpochDay + d);
      return { ...s, slope: { ...s.slope, shrimp } };
    }),
  };
}

/**
 * A v35 or older world with the Deep 7 a new game would have on its day (M192): opened with the world and stepped on
 * their own stream to the save's day. A world with surveys (v27 on) takes their programs' counts on the way, the
 * opening's (the survey before the world's first day, as a new world takes it) and each fall's, after every program the
 * save holds, unless it holds them already (a v29 or older world's surveys are a replay's, `withReplayed`).
 */
function withSlope(world: World): World {
  const ids = DEEP7_SPECIES.map((d) => d.id).filter((id) => world.monitoring && !(id in world.monitoring));
  const counted: Record<string, SurveyResult[]> = Object.fromEntries(ids.map((id) => [id, []]));
  // After the seals, where a new world holds it (its management after it).
  const placed = (s: Sanctuary, slope: Sanctuary['slope']): Sanctuary => { const { management, ...before } = s; return { ...before, slope, management }; };
  const sanctuaries = world.sanctuaries.map((s) => {
    if (!siteHasSlope(s.siteId)) return placed(s, null);
    const slope = openingSlope(world.startEpochDay);
    const opened = ids.length > 0 ? openingMonitoring({ ...world, day: 0, sanctuaries: [{ ...s, slope }] }) : {};
    for (const id of ids) counted[id]?.push(...(opened[id] ?? []));
    for (let d = 1; d <= world.day; d++) {
      stepSlopeDay(slope, world.startEpochDay + d, deep7Normal({ seed: world.seed, day: d }, s.siteId));
      for (const id of ids) {
        const r = surveyOn({ ...world, day: d, sanctuaries: [{ ...s, slope }] }, id);
        if (r) counted[id]?.push(r);
      }
    }
    return placed(s, slope);
  });
  const kept = Object.fromEntries(Object.entries(counted).filter(([, list]) => list.length > 0));
  return Object.keys(kept).length > 0 ? { ...world, sanctuaries, monitoring: withOpeningCounts(world.monitoring, kept, world.startEpochDay) } : { ...world, sanctuaries };
}

/**
 * A save's surveys with the Deep 7's added where a current game holds them: they are the last programs and counted at
 * the opening (their first count dated on or before the world's first day), so after the save's last program counted
 * at the opening and before its first counted later (the honu's, first on 1 August), the save's own in its own order
 * (M192's sabotage record: re-sorting them all hid a migration's own order, M178's).
 */
function withOpeningCounts(saved: World['monitoring'], added: World['monitoring'], startEpochDay: number): World['monitoring'] {
  const keys = Object.keys(saved);
  const later = keys.findIndex((k) => (saved[k]?.[0]?.day ?? Infinity) > startEpochDay);
  const at = later === -1 ? keys.length : later;
  const order = [...keys.slice(0, at), ...Object.keys(added), ...keys.slice(at)];
  return Object.fromEntries(order.map((k) => [k, (added[k] ?? saved[k]) as SurveyResult[]]));
}

/** Whether a sanctuary's whales are whole: none where its site has none; else the humpbacks, every class a whole count not below 0. */
function whalesWhole(w: unknown, hasWhales: boolean): boolean {
  if (!hasWhales) return w === null;
  const x = w as Partial<Whales> | null | undefined;
  const humpback = x?.humpback as Partial<WhaleState> | undefined;
  const counts = humpback?.counts;
  // Its fields counted too (review M116).
  return x !== null && x !== undefined && Object.keys(x).length === 1 && humpback !== undefined && Object.keys(humpback).length === 1 && Array.isArray(counts) && counts.length === whaleClasses(HUMPBACK)
    && counts.every((y) => Number.isInteger(y) && (y as number) >= 0);
}

/** A v11 to v15 world with the humpbacks a new game would have on its day (M101): opened with the world and stepped on their own stream to the save's day. */
function withHumpbacks(world: World): World {
  return {
    ...world,
    sanctuaries: world.sanctuaries.map((s) => {
      if (!siteHasWhales(s.siteId)) return { ...s, whales: null };
      const humpback = openingWhales(world.startEpochDay).humpback;
      for (let d = 1; d <= world.day; d++) stepHumpback(HUMPBACK, humpback, world.startEpochDay + d, HUMPBACK_SUPPLY, whaleDraws({ seed: world.seed, day: d }, s.siteId));
      return { ...s, whales: { humpback } };
    }),
  };
}

/** Whether a sanctuary's seals are whole: none where its site has none; else the monk seals, every class and every day's nursing pups a whole count not below 0. */
function sealsWhole(x: unknown, hasSeals: boolean): boolean {
  if (!hasSeals) return x === null;
  const y = x as Partial<Seals> | null | undefined;
  const monk = y?.monk as Partial<SealState> | undefined;
  const whole = (xs: unknown, n: number): boolean => Array.isArray(xs) && xs.length === n && xs.every((v) => Number.isInteger(v) && (v as number) >= 0);
  return y !== null && y !== undefined && Object.keys(y).length === 1 && monk !== undefined && Object.keys(monk).length === 2
    && whole(monk.counts, sealClasses(MONK_SEAL)) && whole(monk.nursing, MONK_SEAL.nursingDays);
}

/** A v11 to v21 world with the monk seals a new game would have on its day (M103; v17 to v21's made again since v22, M119): opened with the world and stepped on their own stream to the save's day. */
function withMonkSeals(world: World): World {
  return {
    ...world,
    sanctuaries: world.sanctuaries.map((s) => {
      if (!siteHasSeals(s.siteId)) return { ...s, seals: null };
      const monk = openingSeals(world.startEpochDay).monk;
      for (let d = 1; d <= world.day; d++) stepMonkSeals(MONK_SEAL, monk, world.startEpochDay + d, sealDraws({ seed: world.seed, day: d }, s.siteId));
      return { ...s, seals: { monk } };
    }),
  };
}

/** Whether the populations of no sanctuary are whole: the right whales, every class of each sex a whole count not below 0; from v19 the blue marlin too (`blueMarlinWhole`), from v20 the leatherbacks (`leatherbackWhole`), from v21 the gray whales (`grayWhaleWhole`), and nothing else. */
function migrantsWhole(x: unknown, from: number): boolean {
  const y = x as Partial<Migrants> | null | undefined;
  const w = y?.rightWhale as Partial<RightWhaleState> | undefined;
  const whole = (xs: unknown): boolean => Array.isArray(xs) && xs.length === rightWhaleClasses(NORTH_ATLANTIC_RIGHT_WHALE) && xs.every((v) => Number.isInteger(v) && (v as number) >= 0);
  const kinds = from >= 21 ? 4 : from >= 20 ? 3 : from >= 19 ? 2 : 1;
  return y !== null && y !== undefined && Object.keys(y).length === kinds && w !== undefined && Object.keys(w).length === 2 && whole(w.females) && whole(w.males)
    && (from < 19 || blueMarlinWhole(y.blueMarlin)) && (from < 20 || leatherbackWhole(y.leatherback)) && (from < 21 || grayWhaleWhole(y.grayWhale));
}

// Each population's fields counted by the definition of its state's type (the gray whales' 4, the leatherbacks' 3,
// the blue marlin's 3).
/** Whether the gray whales are whole: each sex's classes as many as its ages and its last two seasons' whales of one and over, every one a whole count not below 0, and the season's pregnancy rate a number not below 0. */
function grayWhaleWhole(x: unknown): boolean {
  const g = x as Partial<GrayWhaleState> | null | undefined;
  const whole = (xs: unknown, length: number): boolean => Array.isArray(xs) && xs.length === length && xs.every((v) => Number.isInteger(v) && (v as number) >= 0);
  const classes = grayWhaleClasses(EASTERN_NORTH_PACIFIC_GRAY_WHALE);
  return g !== null && g !== undefined && Object.keys(g).length === 4 && whole(g.females, classes) && whole(g.males, classes)
    && whole(g.recent, 2) && Number.isFinite(g.calvingRate) && (g.calvingRate as number) >= 0;
}

/** Whether the leatherbacks are whole: each sex's classes as many as its ages and each season's nesters, every one a whole count not below 0. */
function leatherbackWhole(x: unknown): boolean {
  const t = x as Partial<LeatherbackState> | null | undefined;
  const whole = (xs: unknown, length: number): boolean => Array.isArray(xs) && xs.length === length && xs.every((v) => Number.isInteger(v) && (v as number) >= 0);
  const classes = leatherbackClasses(WEST_PACIFIC_LEATHERBACK);
  return t !== null && t !== undefined && Object.keys(t).length === 3 && whole(t.females, classes) && whole(t.males, classes)
    && whole(t.nesters, WEST_PACIFIC_LEATHERBACK.seasons.length);
}

/** Whether the blue marlin are whole: each sex's classes as many as its ages, each a whole count not below 0, and the season's spawners a weight not below 0. */
function blueMarlinWhole(x: unknown): boolean {
  const m = x as Partial<BlueMarlinState> | null | undefined;
  const whole = (xs: unknown, classes: number): boolean => Array.isArray(xs) && xs.length === classes && xs.every((v) => Number.isInteger(v) && (v as number) >= 0);
  return m !== null && m !== undefined && Object.keys(m).length === 3 && whole(m.females, PACIFIC_BLUE_MARLIN.female.naturalDeaths.length)
    && whole(m.males, PACIFIC_BLUE_MARLIN.male.naturalDeaths.length) && Number.isFinite(m.spawnersKg) && (m.spawnersKg as number) >= 0;
}

/** A v11 to v17 world with the right whales a new game would have on its day (M107): opened with the world and stepped on their own stream to the save's day, after its regions. */
function withRightWhales(world: World): World {
  const rightWhale = openingRightWhales(world.startEpochDay);
  for (let d = 1; d <= world.day; d++) stepRightWhales(NORTH_ATLANTIC_RIGHT_WHALE, rightWhale, world.startEpochDay + d, RIGHT_WHALE_SUPPLY, rightWhaleDraws({ seed: world.seed, day: d }));
  // The blue marlin follow (`withBlueMarlin`, v18 -> v19).
  return withKeyAfter(world, 'regions', 'migrants', { rightWhale } as Migrants);
}

/** A v11 to v18 world with the blue marlin a new game would have on its day (M108): opened with the world and stepped on their own stream to the save's day, after the right whales. */
function withBlueMarlin(world: World): World {
  const blueMarlin = openingBlueMarlin(world.startEpochDay);
  const young = (kg: number): number => recruitsOf(PACIFIC_BLUE_MARLIN, BLUE_MARLIN_SUPPLY, kg);
  for (let d = 1; d <= world.day; d++) stepBlueMarlin(PACIFIC_BLUE_MARLIN, blueMarlin, world.startEpochDay + d, BLUE_MARLIN_SUPPLY.fishing, young, blueMarlinDraws({ seed: world.seed, day: d }));
  // The leatherbacks follow (`withLeatherbacks`, v19 -> v20).
  return { ...world, migrants: { ...world.migrants, blueMarlin } };
}

/** A v11 to v19 world with the leatherbacks a new game would have on its day (M111): opened with the world and stepped on their own stream to the save's day, after the blue marlin. */
function withLeatherbacks(world: World): World {
  const leatherback = openingLeatherbacks(world.startEpochDay);
  for (let d = 1; d <= world.day; d++) stepLeatherbacks(WEST_PACIFIC_LEATHERBACK, leatherback, world.startEpochDay + d, LEATHERBACK_SUPPLY, leatherbackDraws({ seed: world.seed, day: d }));
  // The gray whales follow (`withGrayWhales`, v20 -> v21).
  return { ...world, migrants: { ...world.migrants, leatherback } };
}

/** A v11 to v20 world with the gray whales a new game would have on its day (M112): opened with the world and stepped on their own stream to the save's day, after the leatherbacks. */
function withGrayWhales(world: World): World {
  const grayWhale = openingGrayWhales(world.startEpochDay);
  for (let d = 1; d <= world.day; d++) stepGrayWhales(EASTERN_NORTH_PACIFIC_GRAY_WHALE, grayWhale, world.startEpochDay + d, GRAY_WHALE_SUPPLY, grayWhaleDraws({ seed: world.seed, day: d }));
  return { ...world, migrants: { ...world.migrants, grayWhale } };
}

/** Whether a place's plankton is whole: each nutrient, living thing and the detritus a number not below 0, and its mixed layer deeper than nothing. */
function planktonWhole(p: unknown): boolean {
  const x = p as Partial<PlanktonState> | null | undefined;
  const amounts = [x?.phosphate, x?.nitrate, x?.phyto, x?.zoo, x?.detritus, x?.fixers];
  return amounts.every((v) => Number.isFinite(v) && (v as number) >= 0) && Number.isFinite(x?.mixedLayerM) && (x?.mixedLayerM as number) > 0;
}

/** A v8 to v28 world's coral's entries in its log, which its replay writes again under the current coral (M49, M167): the bleaching's and the deaths' titles, as `systems/coral.ts` writes them. */
const V8_CORAL_TITLE = /^The corals around .+ are bleaching$|^The heat killed \d+% of the live coral around /;

/** Whether a v6 world's climate, readings and heatwaves are all there, and all numbers where numbers belong. */
function climateWhole(world: World): boolean {
  const c = world.climate as Partial<World['climate']> | undefined;
  const sstWhole = (sst: unknown): boolean => {
    const x = sst as Partial<PlaceSst> | undefined;
    return Number.isInteger(x?.shiftYears) && Number.isFinite(x?.offsetC);
  };
  const heatwaveWhole = (h: unknown): boolean => {
    const x = h as Partial<HeatwaveState> | undefined;
    return Number.isInteger(x?.daysAbove) && Number.isInteger(x?.daysBelow) && Number.isFinite(x?.peakRatio)
      && (x?.startEpochDay === null || Number.isInteger(x?.startEpochDay));
  };
  return Number.isInteger(c?.ensoYear) && Number.isInteger(c?.analogYear)
    && (c?.phase === 'nino' || c?.phase === 'neutral' || c?.phase === 'nina')
    && world.regions.every((r) => sstWhole(r.sst))
    && world.sanctuaries.every((s) => sstWhole(s.sst) && heatwaveWhole(s.heatwave));
}

/**
 * A world from before v11 with the climate, the heat stress, the coral, the
 * plankton, the fish, the honu, the humpbacks, the monk seals and the
 * populations of no sanctuary it would have had (`migrate`): the opening
 * year's climate (`openingClimate`), every place's opening reading,
 * heatwave, heat stress, coral, plankton, fish, honu, humpbacks and monk
 * seals, and the right whales, the blue marlin, the leatherbacks and the gray
 * whales (M107, M108, M111, M112, the populations of no sanctuary, after the
 * regions), then each
 * day from the first to the save's, as the tick steps them
 * (`stepEnvironmentStage`, `stepProductionStage`, `stepAnimalStage`, the
 * fish on each island's own stream of the day, the honu, the humpbacks, the
 * monk seals, the right whales, the blue marlin, the leatherbacks and the
 * gray whales each
 * on its own), on
 * a copy. A world is replayed from its opening: every sanctuary so far opened
 * with it (`establishedDay` 0). Before v6 the climate's stream had not been
 * drawn on, and the save's is where the seed put it; from v6 the seed gives
 * it again (`carriedStreamSeed`).
 *
 * The replay's log entries go in on the days they would have come, before
 * what the player did that day, as a tick's do; those the save already has
 * (a v6 world's climate and heatwaves) are kept once, and a v8 or v9
 * world's coral's are the replay's (v9's titles are v8's). Before v6 the climate, the readings and the
 * heatwaves are the replay's; from v6 they are the save's, and a save whose
 * are not the replay's is damaged; from v7 the heat stress too. The coral,
 * the plankton, the fish, the honu, the humpbacks, the monk seals, the
 * right whales, the blue marlin, the leatherbacks and the gray whales are
 * always the
 * replay's.
 */
function replayEnvironment(world: World, from: number): World {
  const rng = Rng.fromState(from < 6 ? world.climateRngState : carriedStreamSeed(world.seed, STREAM.climate));
  const climate = openingClimate(world.startEpochDay, rng);
  const plankton = openingRegionPlankton(world.startEpochDay);
  const copy: World = {
    ...world,
    climate,
    day: 0,
    // A v10 or older save has no surveys; the animals' stage counts on its programs' days (M160's reviewer: the replay
    // threw on the first one). Its own are given after, `withReplayed`.
    monitoring: {},
    regions: world.regions.map((r) => ({
      id: r.id, env: r.env, sst: openingSst(regionRecord(r.id), climate, world.startEpochDay),
      plankton: plankton[r.id],
    })),
    migrants: openingMigrants(world.startEpochDay),
    sanctuaries: world.sanctuaries.map((s) => {
      const record = sanctuaryRecord(s);
      return {
        ...s,
        sst: openingSst(record, climate, world.startEpochDay),
        heatwave: openingHeatwave(record, world.startEpochDay),
        heatStress: openingHeatStress(record, world.startEpochDay),
        coral: sanctuaryOpeningCoral(s, world.startEpochDay),
        plankton: sanctuaryOpeningPlankton(s.siteId, world.startEpochDay),
        fish: sanctuaryOpeningFish(s.siteId, world.startEpochDay),
        turtles: sanctuaryOpeningTurtles(s.siteId, world.startEpochDay),
        whales: sanctuaryOpeningWhales(s.siteId, world.startEpochDay),
        seals: sanctuaryOpeningSeals(s.siteId, world.startEpochDay),
      };
    }),
  };
  // Each place's opening departure, which the first day's reading carries from.
  copy.regions = copy.regions.map((r) => ({ ...r, env: regionEnvironment(r, world.startEpochDay) }));
  copy.sanctuaries = copy.sanctuaries.map((s) => ({ ...s, env: sanctuaryEnvironment(s, world.startEpochDay) }));
  const events: GameEvent[] = [];
  for (let d = 1; d <= world.day; d++) {
    copy.day = d;
    stepEnvironmentStage(copy, rng, events);
    stepProductionStage(copy, events);
    stepAnimalStage(copy);
  }
  if (from >= 6 && !sameClimate(world, copy, rng.seed, from)) throw damaged(from);
  const out = from < 6
    ? withKeyAfter({ ...world, climateRngState: rng.seed }, 'climateRngState', 'climate', copy.climate)
    : { ...world };
  // Its log is rebuilt by the replay every such world takes after (`withReplayed`, M167), the new game's entries in
  // place of the save's.
  out.regions = world.regions.map((r, i) => {
    const replayed = copy.regions[i] as RegionState;
    return { id: r.id, env: r.env, sst: from < 6 ? replayed.sst : r.sst, plankton: replayed.plankton };
  });
  out.sanctuaries = world.sanctuaries.map((s, i) => {
    const replayed = copy.sanctuaries[i] as Sanctuary;
    const grown = { coral: replayed.coral, plankton: replayed.plankton, fish: replayed.fish, turtles: replayed.turtles, whales: replayed.whales, seals: replayed.seals };
    // The environment's place held for it before the climate's (a v1 world's sanctuaries have none yet).
    if (from < 6) return { ...s, env: s.env, sst: replayed.sst, heatwave: replayed.heatwave, heatStress: replayed.heatStress, ...grown };
    return from < 7 ? { ...s, heatStress: replayed.heatStress, ...grown } : { ...s, ...grown };
  });
  // The populations of no sanctuary (M107), the replay's, after the regions.
  return withKeyAfter(out, 'regions', 'migrants', copy.migrants);
}

/** Whether a replay's climate, stream, readings and heatwaves are the save's, to the bit, and from v7 its heat stress. */
function sameClimate(saved: World, replayed: World, streamState: number, from: number): boolean {
  const same = (a: unknown, b: unknown): boolean => JSON.stringify(a) === JSON.stringify(b);
  const kept = (s: Sanctuary): unknown[] => (from >= 7 ? [s.sst, s.heatwave, s.heatStress] : [s.sst, s.heatwave]);
  return streamState === saved.climateRngState && same(replayed.climate, saved.climate)
    && same(replayed.regions.map((r) => r.sst), saved.regions.map((r) => r.sst))
    && same(replayed.sanctuaries.map(kept), saved.sanctuaries.map(kept));
}

/**
 * A save's log with a replay's: every entry the replay wrote, in the tick's
 * order, and the save's other entries (what the player did, the opening's)
 * each after the replay's entries of its day, as a command comes after the
 * tick of its day. A save's own entries the replay wrote too (a v6 world's
 * climate and heatwaves) are the replay's.
 */
/**
 * A log with events added after each of their days' own, a tick's last system's (the operations', M238); any of them
 * the log holds already (a replay above took them from a current game's world) taken out first.
 */
function withDaysLast(saved: readonly GameEvent[], added: readonly GameEvent[]): GameEvent[] {
  const key = (e: GameEvent): string => JSON.stringify(e);
  const held = new Map<string, number>();
  for (const e of added) held.set(key(e), (held.get(key(e)) ?? 0) + 1);
  const others = saved.filter((e) => {
    const n = held.get(key(e)) ?? 0;
    if (n === 0) return true;
    held.set(key(e), n - 1);
    return false;
  });
  const log: GameEvent[] = [];
  let next = 0;
  for (const e of others) {
    while (next < added.length && (added[next] as GameEvent).day < e.day) log.push(added[next++] as GameEvent);
    log.push(e);
  }
  while (next < added.length) log.push(added[next++] as GameEvent);
  return log;
}

function withReplayedLog(saved: readonly GameEvent[], replayed: readonly GameEvent[]): GameEvent[] {
  const key = (e: GameEvent): string => JSON.stringify(e);
  const written = new Map<string, number>();
  for (const e of replayed) written.set(key(e), (written.get(key(e)) ?? 0) + 1);
  const others = saved.filter((e) => {
    const n = written.get(key(e)) ?? 0;
    if (n === 0) return true;
    written.set(key(e), n - 1);
    return false;
  });
  const log: GameEvent[] = [];
  let next = 0;
  for (const e of others) {
    while (next < replayed.length && (replayed[next] as GameEvent).day <= e.day) log.push(replayed[next++] as GameEvent);
    log.push(e);
  }
  while (next < replayed.length) log.push(replayed[next++] as GameEvent);
  return log;
}

/**
 * The world with a field added where a new world has it, not on the end.
 * Key order is part of what a save says: the determinism tests compare
 * `JSON.stringify` output, which is order sensitive, and a migrated world that
 * is identical to a new one should look identical too (Sable Reserve's save
 * learned this from its columnar grid).
 */
function withKeyAfter<K extends keyof World>(world: World, after: keyof World, key: K, value: World[K]): World {
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(world)) {
    if (k === key) continue;
    out[k] = v;
    if (k === after) out[key] = value;
  }
  return out as unknown as World;
}

function damaged(version: number): Error {
  return new Error(
    `This save (v${version}) is missing part of the world and could not be opened. `
    + 'It is most likely truncated or partly overwritten.',
  );
}
